
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigUpdateRequest} from './ConfigUpdateRequest';
import {FieldModelConfigUpdateRequest} from './ConfigUpdateRequest';
import {EmailTemplateDeleteResponse} from './EmailTemplateDeleteResponse';

/**
 * EmailTemplateDeleteRequest struct
 */
class EmailTemplateDeleteRequest extends ConfigUpdateRequest {
  /**
   * Initialize struct
   * @param {!ConfigUpdateRequest=} parent
   * @param {UUID=} emailTemplateId
   * @param {string=} emailTemplateName
   * @constructor
   */
  constructor (parent = new ConfigUpdateRequest(), emailTemplateId = null, emailTemplateName = null) {
    super()
    super.copy(parent)
    this.EmailTemplateId = emailTemplateId
    this.EmailTemplateName = emailTemplateName
  }

  /**
   * Copy struct (shallow copy)
   * @this {!EmailTemplateDeleteRequest}
   * @param {!EmailTemplateDeleteRequest} other Other struct
   * @returns {!EmailTemplateDeleteRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.EmailTemplateId != null) {
      this.EmailTemplateId = new UUID(other.EmailTemplateId)
    } else {
      this.EmailTemplateId = null
    }
    if (other.EmailTemplateName != null) {
      this.EmailTemplateName = other.EmailTemplateName
    } else {
      this.EmailTemplateName = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!EmailTemplateDeleteRequest}
   * @returns {!EmailTemplateDeleteRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new EmailTemplateDeleteRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new EmailTemplateDeleteRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!EmailTemplateDeleteRequest}
   * @param {!EmailTemplateDeleteRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof EmailTemplateDeleteRequest)) {
      throw new TypeError('Instance of EmailTemplateDeleteRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!EmailTemplateDeleteRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      EmailTemplateId: ((this.EmailTemplateId != null) ? this.EmailTemplateId.toString() : null),
      EmailTemplateName: ((this.EmailTemplateName != null) ? this.EmailTemplateName : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return EmailTemplateDeleteRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!EmailTemplateDeleteRequest} other Object value
   * @returns {!EmailTemplateDeleteRequest} Created struct
   */
  static fromObject (other) {
    return new EmailTemplateDeleteRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!EmailTemplateDeleteRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return EmailTemplateDeleteRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!EmailTemplateDeleteRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6419
  }
}

export { EmailTemplateDeleteRequest };

/**
 * Fast Binary Encoding EmailTemplateDeleteRequest field model
 */
class FieldModelEmailTemplateDeleteRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigUpdateRequest(buffer, 4 + 4)
    this.EmailTemplateId = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.EmailTemplateName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.EmailTemplateId.fbeOffset + this.EmailTemplateId.fbeSize), buffer, this.EmailTemplateId.fbeOffset + this.EmailTemplateId.fbeSize)
  }

  /**
   * Get the ConfigUpdateRequest field model
   * @this {!FieldModelEmailTemplateDeleteRequest}
   * @returns {!FieldModelConfigUpdateRequest} ConfigUpdateRequest field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelEmailTemplateDeleteRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelEmailTemplateDeleteRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.EmailTemplateId.fbeSize + this.EmailTemplateName.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelEmailTemplateDeleteRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.EmailTemplateId.fbeExtra + this.EmailTemplateName.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelEmailTemplateDeleteRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelEmailTemplateDeleteRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelEmailTemplateDeleteRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6419
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelEmailTemplateDeleteRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelEmailTemplateDeleteRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.EmailTemplateId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.EmailTemplateId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.EmailTemplateId.fbeSize

    if ((fbeCurrentSize + this.EmailTemplateName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.EmailTemplateName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.EmailTemplateName.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelEmailTemplateDeleteRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelEmailTemplateDeleteRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelEmailTemplateDeleteRequest}
   * @param {!EmailTemplateDeleteRequest} fbeValue Default value, defaults is new EmailTemplateDeleteRequest()
   * @returns {!EmailTemplateDeleteRequest} EmailTemplateDeleteRequest value
   */
  get (fbeValue = new EmailTemplateDeleteRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelEmailTemplateDeleteRequest}
   * @param {!EmailTemplateDeleteRequest} fbeValue EmailTemplateDeleteRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.EmailTemplateId.fbeSize) <= fbeStructSize) {
      fbeValue.EmailTemplateId = this.EmailTemplateId.get()
    } else {
      fbeValue.EmailTemplateId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.EmailTemplateId.fbeSize

    if ((fbeCurrentSize + this.EmailTemplateName.fbeSize) <= fbeStructSize) {
      fbeValue.EmailTemplateName = this.EmailTemplateName.get()
    } else {
      fbeValue.EmailTemplateName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.EmailTemplateName.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelEmailTemplateDeleteRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelEmailTemplateDeleteRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelEmailTemplateDeleteRequest}
   * @param {!EmailTemplateDeleteRequest} fbeValue EmailTemplateDeleteRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelEmailTemplateDeleteRequest}
   * @param {EmailTemplateDeleteRequest} fbeValue EmailTemplateDeleteRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.EmailTemplateId.set(fbeValue.EmailTemplateId)
    this.EmailTemplateName.set(fbeValue.EmailTemplateName)
  }
}

export { FieldModelEmailTemplateDeleteRequest };

/**
 * Fast Binary Encoding EmailTemplateDeleteRequest model
 */
class EmailTemplateDeleteRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelEmailTemplateDeleteRequest(this.buffer, 4)
  }

  /**
   * Get the EmailTemplateDeleteRequest model
   * @this {!EmailTemplateDeleteRequestModel}
   * @returns {!FieldModelEmailTemplateDeleteRequest} model EmailTemplateDeleteRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!EmailTemplateDeleteRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!EmailTemplateDeleteRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return EmailTemplateDeleteRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!EmailTemplateDeleteRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelEmailTemplateDeleteRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!EmailTemplateDeleteRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!EmailTemplateDeleteRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!EmailTemplateDeleteRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!EmailTemplateDeleteRequestModel}
   * @param {!EmailTemplateDeleteRequest} value EmailTemplateDeleteRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!EmailTemplateDeleteRequestModel}
   * @param {!EmailTemplateDeleteRequest} value EmailTemplateDeleteRequest value, defaults is new EmailTemplateDeleteRequest()
   * @return {!object} Deserialized EmailTemplateDeleteRequest value and its size
   */
  deserialize (value = new EmailTemplateDeleteRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new EmailTemplateDeleteRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new EmailTemplateDeleteRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!EmailTemplateDeleteRequestModel}
   * @param {!number} prev Previous EmailTemplateDeleteRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { EmailTemplateDeleteRequestModel };
EmailTemplateDeleteRequest.__has_response = true;
EmailTemplateDeleteRequest.__response_class = EmailTemplateDeleteResponse;
